import { useEffect, useMemo, useState } from "react";
import { parse } from "date-fns";
import { ConditionalTrialRouting } from "../types/landingPage";
import { QuestionnaireAnswers } from "../components/contact-form/utils/questionnaireStorage";
import {
  TrialSiteWithConsent,
  useNearestTrialSite,
} from "./useNearestTrialSite";
import { calculateAge } from "../utils/dateUtils";

interface UseConditionalTrialSelectionParams {
  enabled: boolean;
  conditionalRouting: ConditionalTrialRouting | undefined;
  questionnaireAnswers: QuestionnaireAnswers;
  countryCode: string;
  postalCode: string | undefined;
  localeCode: string;
  dateOfBirth?: string;
}

interface DeprioritizedFallback {
  trialId: string;
  trialSite: TrialSiteWithConsent;
}

interface UseConditionalTrialSelectionResult {
  selectedTrialId: string | undefined;
  trialSite: TrialSiteWithConsent | undefined;
  loading: boolean;
  error: Error | undefined;
  isLastTrial: boolean;
  attemptedTrials: string[];
  /** Debug info: true if using a deprioritized site as fallback */
  isDeprioritizedFallback: boolean;
  /** Debug info: the deprioritized site that was skipped (if any) */
  skippedDeprioritizedSite?: { trialId: string; siteId: string };
}

/**
 * Hook for conditional trial selection with sequential site validation
 *
 * This hook handles trial routing based on questionnaire answers and site availability:
 * 1. Determines eligible trials based on medical criteria (BMI, conditions, etc.)
 * 2. Tries each trial sequentially, checking if a site is available for the patient's location
 * 3. If a site is found but is in the deprioritized list, saves it as fallback and continues
 * 4. Returns the first trial with an available non-deprioritized site
 * 5. Falls back to deprioritized site if no other options exist
 *
 * Used for landing pages where multiple trials have different eligibility criteria
 * and site availability varies by location.
 */
export function useConditionalTrialSelection({
  enabled,
  conditionalRouting,
  questionnaireAnswers,
  countryCode,
  postalCode,
  localeCode,
  dateOfBirth,
}: UseConditionalTrialSelectionParams): UseConditionalTrialSelectionResult {
  // Determine eligible trials based on questionnaire answers
  const eligibleTrials = useMemo(() => {
    if (!enabled || !conditionalRouting) return [];

    // Combine questionAnswers with BMI (which is stored at root level)
    const answers = {
      ...(questionnaireAnswers.questionAnswers || {}),
      bmi: questionnaireAnswers.bmi,
    };

    let age: number | undefined;
    if (dateOfBirth) {
      const parsed = parse(dateOfBirth, "MM/dd/yyyy", new Date());
      if (!isNaN(parsed.getTime())) {
        age = calculateAge(parsed);
      }
    }

    return conditionalRouting.getEligibleTrials(answers, age);
  }, [enabled, conditionalRouting, questionnaireAnswers, dateOfBirth]);

  // Create a Set of deprioritized site IDs for efficient lookup
  const deprioritizedSiteIds = useMemo(() => {
    const siteMap = new Map<string, Set<string>>();
    if (conditionalRouting?.deprioritizedSites) {
      for (const config of conditionalRouting.deprioritizedSites) {
        siteMap.set(config.trialId, new Set(config.siteIds));
      }
    }
    return siteMap;
  }, [conditionalRouting?.deprioritizedSites]);

  // Track which trial we're currently attempting
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  const currentTrialId = eligibleTrials[currentTrialIndex];

  // Track deprioritized fallback (first deprioritized site found)
  const [deprioritizedFallback, setDeprioritizedFallback] =
    useState<DeprioritizedFallback | null>(null);

  // Track skipped deprioritized site for debug purposes
  const [skippedDeprioritizedSite, setSkippedDeprioritizedSite] = useState<
    { trialId: string; siteId: string } | undefined
  >(undefined);

  // Query current trial's nearest site
  const {
    value: trialSite,
    loading,
    error,
  } = useNearestTrialSite(
    enabled ? currentTrialId : undefined,
    countryCode,
    postalCode,
    localeCode,
  );

  // Check if current site is deprioritized
  const isCurrentSiteDeprioritized = useMemo(() => {
    if (!trialSite || !currentTrialId) return false;
    const trialDeprioritizedSites = deprioritizedSiteIds.get(currentTrialId);
    return trialDeprioritizedSites?.has(String(trialSite.id)) ?? false;
  }, [trialSite, currentTrialId, deprioritizedSiteIds]);

  // Handle site found - check if deprioritized and move to next trial if needed
  useEffect(() => {
    if (!enabled || loading || error) return;

    // If we found a site but it's deprioritized
    if (trialSite && isCurrentSiteDeprioritized) {
      // Save as fallback if we don't have one yet
      if (!deprioritizedFallback) {
        setDeprioritizedFallback({
          trialId: currentTrialId,
          trialSite: trialSite,
        });
        setSkippedDeprioritizedSite({
          trialId: currentTrialId,
          siteId: String(trialSite.id),
        });
      }

      // Move to next trial if available
      if (currentTrialIndex < eligibleTrials.length - 1) {
        setCurrentTrialIndex((prev) => prev + 1);
      }
      return;
    }

    // If no site found (and not deprioritized case), move to next trial
    if (!trialSite && currentTrialIndex < eligibleTrials.length - 1) {
      setCurrentTrialIndex((prev) => prev + 1);
    }
  }, [
    enabled,
    loading,
    trialSite,
    error,
    currentTrialIndex,
    eligibleTrials.length,
    isCurrentSiteDeprioritized,
    deprioritizedFallback,
    currentTrialId,
  ]);

  // Reset state when eligible trials change
  useEffect(() => {
    setCurrentTrialIndex(0);
    setDeprioritizedFallback(null);
    setSkippedDeprioritizedSite(undefined);
  }, [eligibleTrials]);

  // Determine final result
  const isLastTrial = currentTrialIndex === eligibleTrials.length - 1;

  // Use deprioritized fallback only if:
  // 1. We're on the last trial AND
  // 2. Either no site found OR current site is also deprioritized AND
  // 3. We have a fallback saved
  const shouldUseFallback =
    isLastTrial &&
    (!trialSite || isCurrentSiteDeprioritized) &&
    deprioritizedFallback !== null;

  const finalTrialId = shouldUseFallback
    ? deprioritizedFallback?.trialId
    : trialSite && !isCurrentSiteDeprioritized
      ? currentTrialId
      : undefined;

  const finalTrialSite = shouldUseFallback
    ? deprioritizedFallback?.trialSite
    : trialSite && !isCurrentSiteDeprioritized
      ? trialSite
      : undefined;

  return {
    selectedTrialId: finalTrialSite ? finalTrialId : undefined,
    trialSite: finalTrialSite,
    loading,
    error,
    isLastTrial,
    attemptedTrials: eligibleTrials.slice(0, currentTrialIndex + 1),
    isDeprioritizedFallback: shouldUseFallback,
    skippedDeprioritizedSite,
  };
}
